import { cn } from '@/utils/styles';
import { CheckIcon, OctagonAlertIcon } from 'lucide-react';
import React, { useState } from 'react';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '../Form';
import { Input } from '../Input';

export interface TextFieldProps extends React.ComponentProps<typeof Input> {
  name: string;
  hideStatus?: boolean;
  label?: React.ReactNode;
  description?: React.ReactNode;
  showFormMessage?: boolean;
  hideValidationIcon?: boolean;
  style?: React.CSSProperties;
  numericOnly?: boolean;
}

export const TextField = ({
  name,
  label,
  children,
  className,
  hideStatus,
  description,
  placeholder,
  showFormMessage = true,
  hideValidationIcon = false,
  numericOnly = false,
  ...rest
}: TextFieldProps) => {
  const formField = useFormField();
  const [isTyping, setIsTyping] = useState(false);
  const emailRegex = /^\S+@\S+\.\S+$/;

  return (
    <FormField
      name={name}
      control={formField.control}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <Input
              {...rest}
              className={cn(label && 'pt-6 pr-11 h-14 pb-1')}
              onInput={
                numericOnly
                  ? (event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value } = event.target;
                      event.target.value = value.replace(/[^0-9]/g, '');
                      if (rest.onInput) {
                        rest.onInput(event);
                      }
                      field.onChange(event);
                    }
                  : rest.onInput
              }
              onChange={(e) => {
                setIsTyping(true);
                field.onChange(e);
                if (rest?.onChange) rest.onChange(e);
              }}
              onBlur={() => setIsTyping(false)}
              placeholder={placeholder}
              value={field.value}
            >
              {label && (
                <FormLabel className="absolute top-2 typography-btn-small">
                  {label}
                </FormLabel>
              )}

              {fieldState.isDirty && !hideStatus && !isTyping && (
                <span className="flex items-center absolute right-4 top-0 bottom-0">
                  {name === 'email' ? (
                    emailRegex.test(field.value) ? (
                      <CheckIcon size={14} className="text-success" />
                    ) : (
                      <OctagonAlertIcon size={14} className="text-error" />
                    )
                  ) : fieldState.invalid ? (
                    <OctagonAlertIcon size={14} className="text-error" />
                  ) : rest?.minLength ? (
                    field.value.length === rest.minLength ? (
                      <CheckIcon size={14} className="text-success" />
                    ) : (
                      <OctagonAlertIcon size={14} className="text-error" />
                    )
                  ) : null}
                </span>
              )}

              {children}
            </Input>
          </FormControl>

          {description && <FormDescription>{description}</FormDescription>}

          {showFormMessage && <FormMessage />}
        </FormItem>
      )}
    />
  );
};
